import React, { useRef, useEffect } from 'react';

const BAR_COUNT = 28;
const MIN_BAR_HEIGHT = 2;

/** Cross-browser rounded rect helper */
const drawRoundedRect = (ctx, x, y, w, h, r) => {
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

/**
 * Real-time frequency bar waveform canvas.
 *
 * @param {boolean} isRecording - Whether recording is active.
 * @param {{ current: AnalyserNode|null }} analyserNode - Ref to the Web Audio AnalyserNode.
 */
const WaveformCanvas = ({ isRecording, analyserNode }) => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!isRecording || !analyserNode?.current) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      return;
    }

    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    const W = canvas.offsetWidth;
    const H = canvas.offsetHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    const analyser = analyserNode.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const usedBins = Math.floor(bufferLength * 0.75);
    const binsPerBar = Math.floor(usedBins / BAR_COUNT);
    const gap = 4;
    const barWidth = (W - gap * (BAR_COUNT - 1)) / BAR_COUNT;
    const midY = H / 2;

    const draw = () => {
      animationFrameRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);

      ctx.clearRect(0, 0, W, H);

      for (let i = 0; i < BAR_COUNT; i++) {
        let sum = 0;
        for (let j = 0; j < binsPerBar; j++) {
          sum += dataArray[i * binsPerBar + j];
        }
        const avg = sum / binsPerBar;
        const barHalf = Math.max(MIN_BAR_HEIGHT, (avg / 255) * midY * 0.88);
        const x = i * (barWidth + gap);
        const radius = Math.min(barWidth / 2, 3);

        const gradTop = ctx.createLinearGradient(0, midY - barHalf, 0, midY);
        gradTop.addColorStop(0, 'rgba(239, 68, 68, 0.95)');
        gradTop.addColorStop(1, 'rgba(239, 68, 68, 0.25)');
        ctx.fillStyle = gradTop;
        drawRoundedRect(ctx, x, midY - barHalf, barWidth, barHalf, radius);
        ctx.fill();

        const gradBot = ctx.createLinearGradient(0, midY, 0, midY + barHalf);
        gradBot.addColorStop(0, 'rgba(239, 68, 68, 0.25)');
        gradBot.addColorStop(1, 'rgba(239, 68, 68, 0.95)');
        ctx.fillStyle = gradBot;
        drawRoundedRect(ctx, x, midY, barWidth, barHalf, radius);
        ctx.fill();
      }
    };

    draw();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isRecording, analyserNode]);

  return (
    <canvas
      ref={canvasRef}
      className="w-[280px] max-md:w-[220px] h-[64px] max-md:h-[52px] rounded-xl"
      style={{ background: 'rgba(239,68,68,0.06)' }}
    />
  );
};

export default WaveformCanvas;
